import type { AppState } from '../state/store';
import { versionedAssetUrl } from '../assets/version';
import { btn, esc } from './components';

/**
 * Public player-facing Roadmap: curated milestones only, written for players.
 * This is hand-written copy — it deliberately does not render any planning
 * document, and it must never mention ops/engineering work. A test guards the
 * rendered output against that vocabulary.
 */

export type RoadmapStatus = 'in-design' | 'planned' | 'exploring' | 'later';

const STATUS_LABELS: Record<RoadmapStatus, string> = {
  'in-design': 'In design',
  planned: 'Planned',
  exploring: 'Exploring',
  later: 'Later',
};

interface RoadmapMilestone {
  id: string;
  icon: string;
  kicker: string;
  title: string;
  status: RoadmapStatus;
  summary: string;
  points: string[];
  why: string;
}

const MILESTONES: RoadmapMilestone[] = [
  {
    id: 'hearth-chamber',
    icon: '/assets/sprites/hub/hub_prop_brazier_01.png',
    kicker: 'Housing',
    title: 'The Hearth Chamber',
    status: 'in-design',
    summary: 'A personal room of your own, just off the Waystation — somewhere to be between descents.',
    points: [
      'Every runner gets a chamber to furnish and arrange however they like.',
      'Collect furniture, lanterns, rugs, and trophies, bought with Gold.',
      'Decor is visual and prestige only — nothing in your room changes your power in the dungeon.',
    ],
    why: 'Gold earned below deserves somewhere to go besides repairs. The Hearth Chamber turns your best hauls into a home worth showing.',
  },
  {
    id: 'sunless-stair',
    icon: '/assets/sprites/environment/props/prop_descent_stair_01.png',
    kicker: 'Challenge mode',
    title: 'The Sunless Stair',
    status: 'planned',
    summary: 'A deep-descent challenge with one question at the bottom: how far down can you go?',
    points: [
      'Enter with your own loadout — the gear you have earned and the supplies you pack matter.',
      'Floors keep going down, and every one asks more of your build and your nerve.',
      'A leaderboard tracks who pushed deepest.',
      'Rewards are badges, titles, and exclusive cosmetics — prestige, never a Gold shower.',
    ],
    why: 'An endgame proving ground for runners who want to test everything they have put together — without touching the economy.',
  },
  {
    id: 'seasons-favor',
    icon: '/assets/sprites/hub/hub_prop_wax_seal_01.png',
    kicker: 'Recurring goals',
    title: 'Seasons & Waystation Favor',
    status: 'planned',
    summary: 'Named seasons bring weekly objectives, rotating rewards, and a reason to check the notice board.',
    points: [
      'Weekly objectives that route through real play — descend, extract, push deeper.',
      'Taking part earns Waystation Favor, spent on decor, emotes, titles, and cosmetics in a season shop.',
      'Favor is earned, never bought: it cannot be traded, cannot become Gold, and cannot buy combat power.',
    ],
    why: 'A steady rhythm of fresh goals and collectibles, with prestige as the prize — never pay-to-win.',
  },
  {
    id: 'visits-sparring',
    icon: '/assets/sprites/hub/hub_prop_signpost_01.png',
    kicker: 'Social',
    title: 'Visits & Sparring',
    status: 'exploring',
    summary: 'The Waystation is better with neighbors.',
    points: [
      'Visit other runners’ Hearth Chambers and see what their Gold built.',
      'Sparring: challenge a rival, each take on the same descent in your own time, then compare results and watch their ghost.',
      'No live duels, no wagers, no item loss — bragging rights only.',
    ],
    why: 'Identity and rivalry without risk: nothing you own is ever on the line.',
  },
  {
    id: 'later-horizons',
    icon: '/assets/sprites/hub/hub_prop_lantern_post_01.png',
    kicker: 'Further out',
    title: 'Later Horizons',
    status: 'later',
    summary: 'Further down the road, and more subject to change than anything above.',
    points: [
      'The Sunless Stair descending even deeper once the first ladders settle.',
      'Select decor becoming tradeable on the Marketplace.',
      'Turning duplicate cosmetics into keepsake variants of past pieces.',
      'Season finales that close each season with a proper send-off.',
    ],
    why: 'Where the road goes after that depends on how the earlier milestones land — and on what you tell us.',
  },
];

const DISCLAIMER = 'Roadmap plans may shift as we test balance, economy, and live feedback.';

function sprite(icon: string): string {
  return `<img class="roadmap-sprite" src="${esc(versionedAssetUrl(icon))}" alt="" loading="lazy" decoding="async" aria-hidden="true">`;
}

function statusBadge(status: RoadmapStatus): string {
  return `<span class="roadmap-status roadmap-status--${esc(status)}">${esc(STATUS_LABELS[status])}</span>`;
}

function hero(): string {
  return `
    <header class="roadmap-hero">
      <p class="docs-section__eyebrow">The road ahead</p>
      <h1>Gravelight Roadmap</h1>
      <p class="roadmap-hero__pitch">What’s next for the Waystation, the dungeon, and the economy.</p>
      <p class="roadmap-hero__flavor">
        The lantern is lit and the ferry keeps its schedule. Here is what we are building next,
        in the order we expect to descend.
      </p>
      <p class="roadmap-disclaimer">${esc(DISCLAIMER)}</p>
    </header>`;
}

function milestoneCard(milestone: RoadmapMilestone): string {
  const points = milestone.points.map((point) => `<li>${esc(point)}</li>`).join('');
  return `
    <li class="roadmap-stop roadmap-stop--${esc(milestone.status)}" id="${esc(milestone.id)}">
      <article class="roadmap-card">
        <header class="roadmap-card__head">
          ${sprite(milestone.icon)}
          <div class="roadmap-card__title">
            <p class="roadmap-card__kicker">${esc(milestone.kicker)}</p>
            <h2>${esc(milestone.title)}</h2>
          </div>
          ${statusBadge(milestone.status)}
        </header>
        <p class="roadmap-card__summary">${esc(milestone.summary)}</p>
        <ul class="roadmap-card__points">${points}</ul>
        <p class="roadmap-why"><strong>Why it matters:</strong> ${esc(milestone.why)}</p>
      </article>
    </li>`;
}

function footer(state: AppState): string {
  const playCta = state.session
    ? btn('Enter the Waystation', 'goto-waystation', {}, { variant: 'primary' })
    : btn('Connect Wallet', 'goto-dashboard', {}, { variant: 'primary' });
  return `
    <footer class="roadmap-foot">
      <p>${esc(DISCLAIMER)} The dungeon is open in the meantime.</p>
      <div class="btn-row">
        ${playCta}
        ${btn('How To Play', 'goto-how-to-play', {}, { variant: 'ghost' })}
        ${btn('Full Docs', 'goto-docs', {}, { variant: 'ghost' })}
      </div>
    </footer>`;
}

export function renderRoadmap(state: AppState): string {
  return `
    <div class="roadmap-shell">
      ${hero()}
      <ol class="roadmap-track" aria-label="Upcoming Gravelight milestones">
        ${MILESTONES.map(milestoneCard).join('')}
      </ol>
      ${footer(state)}
    </div>`;
}
